import type { Feature, FeatureCollection, Point } from 'geojson';
import { GAUGE_CACHE } from '../config/gauges';
import { fetchJson } from '../lib/fetchJson';
import { regionToBBox, type Region } from '../lib/geo';
import type { Gauge } from '../types/gauges';

const BASE = 'https://mapservices.weather.noaa.gov/static/rest/services/nws_reference_maps/USGS_Stream_Gauges/MapServer/0/query';

interface ArcGISGaugeProperties {
  OBJECTID?: number;
  site_no?: string | null;
  SITE_NO?: string | null;
  site_name?: string | null;
  STATION_NM?: string | null;
  da_sqkm?: number | null;
  feature_id?: number | null;
  lat?: number | null;
  lon?: number | null;
  site_id?: string | null;
}

interface ArcGISPage extends FeatureCollection<Point, ArcGISGaugeProperties> {
  /** Set by ArcGIS when the page was truncated at maxRecordCount. */
  exceededTransferLimit?: boolean;
  error?: { code: number; message: string };
}

export function gaugePageUrl(region: Region, page: number, pageSize = GAUGE_CACHE.arcgisPageSize): string {
  const b = regionToBBox(region);
  return (
    `${BASE}?where=1=1&outFields=*&f=geojson` +
    `&geometry=${b.minLon},${b.minLat},${b.maxLon},${b.maxLat}` +
    '&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects' +
    '&orderByFields=OBJECTID' +
    `&resultOffset=${page * pageSize}&resultRecordCount=${pageSize}`
  );
}

export function gaugeFromFeature(f: Feature<Point, ArcGISGaugeProperties>): Gauge | null {
  const coords = f.geometry?.coordinates;
  if (!coords || coords.length !== 2) return null;
  const p = f.properties ?? {};
  return {
    id: p.site_no ?? p.SITE_NO ?? crypto.randomUUID(),
    name: p.site_name ?? p.STATION_NM ?? 'Unknown Gauge',
    lat: coords[1],
    lng: coords[0],
    drainageAreaKm: p.da_sqkm ?? null,
    observationSeverity: null,
    forecastSeverity: null,
    forecastPeakTime: null,
    forecastIsRising: null,
    nextStage: null,
    nextStageTime: null,
    hoursToCrest: null,
    hoursToNextStage: null,
  };
}

/**
 * Gauge points for a region, paged because the layer caps responses at 2,000
 * features ordered by OBJECTID and silently drops the rest (GaugeManager.swift
 * 337-394).
 */
export async function fetchNOAAGauges(region: Region, signal?: AbortSignal): Promise<Gauge[]> {
  const gauges: Gauge[] = [];
  for (let page = 0; page < GAUGE_CACHE.arcgisMaximumPages; page++) {
    signal?.throwIfAborted();
    const data = await fetchJson<ArcGISPage>(gaugePageUrl(region, page), { signal, timeoutMs: 60_000 });
    if (data.error) throw new Error(`ArcGIS error ${data.error.code}: ${data.error.message}`);
    for (const f of data.features ?? []) {
      const g = gaugeFromFeature(f);
      if (g) gauges.push(g);
    }
    if (!data.exceededTransferLimit) break;
  }
  return gauges;
}
